/*
  CatchFish product page scripts (product pages only).

  <cf-gallery>
  - Native scroll-snap slider: touch swipe works without JS; JS only syncs
    the thumbnails, counter and dots, and handles prev/next + keyboard.
  - Fullscreen view uses a native <dialog>.
  - Follows the selected variant's featured media (PUB_SUB_EVENTS.variantChange).
  - Custom slide types: CfGallery.registerSlide(type, { activate, deactivate }),
    matched on data-media-type. Nothing is registered today.
  - 3D View: only present when snippets/cf-3d-config.liquid has a live, real
    model for the product. Pressing it loads assets/cf-3d.js (which then loads
    the viewer library and the model); nothing 3D is downloaded before that.
    CfGallery.resolve3d(config, variantId) is the single variant -> model rule.
  - View angles (photo viewer): when the product has a photo-viewer entry and no
    live 3D model, the pill opens assets/cf-photoview.js (loaded on click):
    real photos only, drag between views, zoom, full screen.

  Variant labels
  - Updates the "selected value" text next to each option name immediately,
    before product-info.js re-renders the picker.
*/
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const slideHandlers = new Map();

  // Loads a script once; used only for the on-demand 3D viewer.
  const scripts = new Map();
  const loadScript = (src) => {
    if (!scripts.has(src)) {
      scripts.set(
        src,
        new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.onload = resolve;
          script.onerror = () => {
            scripts.delete(src);
            reject(new Error(`Could not load ${src}`));
          };
          document.head.appendChild(script);
        })
      );
    }
    return scripts.get(src);
  };

  // Variant -> 3D model/material, from the product's "mapping" rules (first match wins).
  // Returns null when that variant has no real model, so no 3D is offered for it.
  const norm = (value) => String(value == null ? '' : value).trim().toLowerCase();
  const resolve3d = (config, variantId) => {
    const product = config && config.product;
    if (!product || !product.models) return null;
    const options = (config.variants && config.variants[String(variantId)]) || [];
    const names = config.optionNames || [];
    const rules = product.mapping || [{ match: {}, model: Object.keys(product.models)[0] }];
    for (const rule of rules) {
      const match = rule.match || {};
      const matches = Object.keys(match).every((key) => {
        if (key === 'variant') return String(match[key]) === String(variantId);
        const index = names.findIndex((name) => norm(name) === norm(key));
        return index >= 0 && norm(options[index]) === norm(match[key]);
      });
      if (!matches) continue;
      const model = rule.model ? product.models[rule.model] : null;
      if (!model || !model.src) return null;
      return { key: rule.model, model, material: rule.material || model.material || null };
    }
    return null;
  };

  class CfGallery extends HTMLElement {
    static registerSlide(type, handler) {
      slideHandlers.set(type, handler);
    }

    static resolve3d(config, variantId) {
      return resolve3d(config, variantId);
    }

    static loadScript(src) {
      return loadScript(src);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-cf-viewport]');
      if (!this.viewport) return;
      this.slides = Array.from(this.viewport.querySelectorAll('[data-cf-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-cf-thumb]'));
      this.currentLabel = this.querySelector('[data-cf-current]');
      this.dialog = this.querySelector('[data-cf-lightbox]');
      this.index = 0;
      this.scrollLock = 0;

      this.querySelector('[data-cf-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-cf-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => {
          this.close3d(false);
          this.closePhotoView(false);
          this.go(Number(thumb.dataset.cfThumb));
        })
      );
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.index - 1);
        }
      });

      // Sync the active slide from the scroll position (swipe, trackpad, buttons).
      let ticking = false;
      this.viewport.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            if (Date.now() < this.scrollLock) return;
            const index = this.indexFromScroll();
            if (index !== this.index) this.setActive(index, false);
          });
        },
        { passive: true }
      );

      this.querySelectorAll('[data-cf-open]').forEach((button) =>
        button.addEventListener('click', () => {
          const value = button.dataset.cfOpen;
          this.openLightbox(value === 'current' ? this.index : Number(value));
        })
      );
      this.setupLightbox();

      const initial = this.dataset.initialMedia;
      const start = initial ? this.slides.findIndex((slide) => slide.dataset.mediaId === initial) : 0;
      if (start > 0) this.go(start, false);
      else this.setActive(0, false);

      this.onPageShow = (event) => { if (event.persisted) this.resync(); };
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('orientationchange', () => setTimeout(() => this.resync(), 250));
      if ('ResizeObserver' in window) {
        let lastWidth = this.viewport.clientWidth;
        this.sizeObserver = new ResizeObserver(() => {
          const width = this.viewport.clientWidth;
          if (width && width !== lastWidth) {
            lastWidth = width;
            this.resync();
          }
        });
        this.sizeObserver.observe(this.viewport);
      }
      // The first image can finish decoding after the initial position is set.
      this.slides[0]?.querySelector('img')?.addEventListener('load', () => this.resync(), { once: true });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || data.sectionId !== this.dataset.section) return;
          const mediaId = data.variant && data.variant.featured_media && data.variant.featured_media.id;
          if (mediaId) this.showMedia(String(mediaId));
          if (data.variant) {
            this.refresh3d(data.variant.id);
            if (this.photoView) this.photoView.setVariant(String(data.variant.id));
          }
        });
      }

      this.setup3d();
      this.setupPhotoView();
    }

    disconnectedCallback() {
      this.close3d(false);
      this.closePhotoView(false);
      if (this.unsubscribe) this.unsubscribe();
      if (this.sizeObserver) this.sizeObserver.disconnect();
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
    }

    // ----- 3D View (enhancement only; the photo gallery never depends on it) -----
    setup3d() {
      const button = this.querySelector('[data-cf-3d-open]');
      if (!button) return;
      let config = null;
      try {
        config = JSON.parse(this.querySelector('script[data-cf-3d-config]').textContent);
      } catch (error) {
        config = null;
      }
      // The viewer needs WebGL 2. Checking the constructor creates no WebGL context.
      if (!config || !config.assets || typeof window.WebGL2RenderingContext === 'undefined') {
        button.hidden = true;
        return;
      }
      this.button3d = button;
      this.config3d = config;
      button.addEventListener('click', () => (this.viewer3d ? this.close3d() : this.open3d()));
      this.refresh3d(this.currentVariantId());
    }

    currentVariantId() {
      const input = document.querySelector(`#product-form-${this.dataset.section} input[name="id"]`);
      return input && input.value ? input.value : null;
    }

    refresh3d(variantId) {
      if (!this.button3d || !variantId) return;
      this.variant3d = String(variantId);
      if (this.viewer3d) this.viewer3d.setVariant(this.variant3d);
      else this.button3d.hidden = !resolve3d(this.config3d, this.variant3d);
    }

    open3d() {
      if (!this.button3d || this.viewer3d || this.opening3d) return;
      this.opening3d = true;
      const button = this.button3d;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      loadScript(this.config3d.assets.engine)
        .then(() =>
          window.Cf3d.open(this, this.config3d, {
            variantId: this.currentVariantId() || this.variant3d,
            onClose: () => {
              this.viewer3d = null;
              this.refresh3d(this.currentVariantId());
            },
          })
        )
        .then((viewer) => {
          this.viewer3d = viewer;
        })
        .catch((error) => console.warn('[CatchFish 3D]', error))
        .finally(() => {
          this.opening3d = false;
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        });
    }

    close3d(restoreFocus = true) {
      if (this.viewer3d) this.viewer3d.close({ focus: restoreFocus });
    }

    // ----- View angles: interactive viewer of the real product photos -----
    setupPhotoView() {
      const button = this.querySelector('[data-cf-pv-open]');
      if (!button) return;
      let config = null;
      try {
        config = JSON.parse(this.querySelector('script[data-cf-pv-config]').textContent);
      } catch (error) {
        config = null;
      }
      if (!config || !config.assets) return;
      this.pvButton = button;
      this.pvConfig = config;
      button.addEventListener('click', () => (this.photoView ? this.closePhotoView() : this.openPhotoView()));
    }

    openPhotoView() {
      if (!this.pvButton || this.photoView || this.openingPv) return;
      this.openingPv = true;
      const button = this.pvButton;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      loadScript(this.pvConfig.assets.engine)
        .then(() =>
          window.CfPhotoView.open(this, this.pvConfig, {
            variantId: this.currentVariantId(),
            onClose: () => {
              this.photoView = null;
            },
          })
        )
        .then((viewer) => {
          this.photoView = viewer;
        })
        .catch((error) => console.warn('[CatchFish photo viewer]', error))
        .finally(() => {
          this.openingPv = false;
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        });
    }

    closePhotoView(restoreFocus = true) {
      if (this.photoView) this.photoView.close({ focus: restoreFocus });
    }

    showMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (index >= 0) this.go(index);
    }

    go(index, smooth = true) {
      if (!this.slides.length) return;
      const count = this.slides.length;
      const target = ((index % count) + count) % count;
      const animate = smooth && !reduceMotion();
      this.scrollLock = animate ? Date.now() + 700 : 0;
      // Scroll to the slide's own offset. Index x clientWidth breaks whenever
      // the measured width is stale (first paint, rotation, bfcache restore).
      this.viewport.scrollTo({ left: this.offsetOf(target), behavior: animate ? 'smooth' : 'auto' });
      this.setActive(target, true);
    }

    offsetOf(index) {
      const slide = this.slides[index];
      return slide ? slide.offsetLeft - this.slides[0].offsetLeft : index * this.viewport.clientWidth;
    }

    // Index of the slide currently aligned in the viewport, by real geometry.
    indexFromScroll() {
      const left = this.viewport.scrollLeft;
      let best = 0;
      let bestDistance = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(this.offsetOf(i) - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    // Put the active slide back in place after a layout change.
    resync() {
      if (!this.slides.length) return;
      const left = this.offsetOf(this.index);
      if (Math.abs(this.viewport.scrollLeft - left) > 1) {
        this.viewport.scrollTo({ left, behavior: 'auto' });
      }
    }

    setActive(index, fromControl) {
      const previous = this.slides[this.index];
      this.index = index;
      const slide = this.slides[index];

      this.thumbs.forEach((thumb) => {
        if (Number(thumb.dataset.cfThumb) === index) {
          thumb.setAttribute('aria-current', 'true');
          const rail = thumb.closest('[data-cf-thumbs]');
          if (rail && rail.scrollHeight + rail.scrollWidth > rail.clientHeight + rail.clientWidth + 4) {
            const vertical = rail.scrollHeight > rail.clientHeight + 2;
            const railBox = rail.getBoundingClientRect();
            const box = thumb.getBoundingClientRect();
            const offset = vertical
              ? rail.scrollTop + box.top - railBox.top - rail.clientHeight / 2 + box.height / 2
              : rail.scrollLeft + box.left - railBox.left - rail.clientWidth / 2 + box.width / 2;
            rail.scrollTo({ [vertical ? 'top' : 'left']: offset, behavior: reduceMotion() ? 'auto' : 'smooth' });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (this.currentLabel) this.currentLabel.textContent = String(index + 1);

      // Pause media that scrolled away; let custom slide types react.
      if (previous && previous !== slide) {
        previous.querySelectorAll('video').forEach((video) => video.pause());
        slideHandlers.get(previous.dataset.mediaType)?.deactivate?.(previous);
      }
      if (slide) slideHandlers.get(slide.dataset.mediaType)?.activate?.(slide, { fromControl });
    }

    setupLightbox() {
      const dialog = this.dialog;
      if (!dialog) return;
      this.lbViewport = dialog.querySelector('[data-cf-lb-viewport]');
      this.lbCurrent = dialog.querySelector('[data-cf-lb-current]');
      this.lbSlides = Array.from(dialog.querySelectorAll('.cf-lightbox__slide'));

      dialog.querySelector('[data-cf-close]')?.addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-cf-lb-prev]')?.addEventListener('click', () => this.lbGo(this.lbIndex - 1));
      dialog.querySelector('[data-cf-lb-next]')?.addEventListener('click', () => this.lbGo(this.lbIndex + 1));
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.lbGo(this.lbIndex + 1);
        if (event.key === 'ArrowLeft') this.lbGo(this.lbIndex - 1);
      });
      // Click on the dark backdrop (outside the image) closes.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.classList.contains('cf-lightbox__slide')) dialog.close();
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('cf-lightbox-open');
        if (this.lbIndex !== undefined && this.lbIndex !== this.index) this.go(this.lbIndex, false);
        this.querySelector('[data-cf-open="current"]')?.focus({ preventScroll: true });
      });

      let ticking = false;
      this.lbViewport?.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const width = this.lbViewport.clientWidth || 1;
            this.lbIndex = Math.round(this.lbViewport.scrollLeft / width);
            if (this.lbIndex < 0) this.lbIndex = 0;
            if (this.lbCurrent) this.lbCurrent.textContent = String(this.lbIndex + 1);
          });
        },
        { passive: true }
      );
    }

    openLightbox(index) {
      const dialog = this.dialog;
      if (!dialog || typeof dialog.showModal !== 'function') return;
      // Images inside the dialog load only when it opens.
      this.lbSlides.forEach((slide) => slide.querySelector('img')?.setAttribute('loading', 'eager'));
      document.documentElement.classList.add('cf-lightbox-open');
      dialog.showModal();
      this.lbIndex = index;
      requestAnimationFrame(() => {
        this.lbViewport.scrollTo({ left: index * this.lbViewport.clientWidth, behavior: 'auto' });
        if (this.lbCurrent) this.lbCurrent.textContent = String(index + 1);
        this.lbViewport.focus({ preventScroll: true });
      });
    }

    lbGo(index) {
      const count = this.lbSlides.length;
      if (!count) return;
      const target = ((index % count) + count) % count;
      this.lbIndex = target;
      this.lbViewport.scrollTo({
        left: target * this.lbViewport.clientWidth,
        behavior: reduceMotion() ? 'auto' : 'smooth',
      });
      if (this.lbCurrent) this.lbCurrent.textContent = String(target + 1);
    }
  }

  if (!customElements.get('cf-gallery')) {
    customElements.define('cf-gallery', CfGallery);
    window.CfGallery = CfGallery;
  }

  // Desktop: keep the info panel sticky only when it fits in the window,
  // otherwise its lower part (Add to cart, trust row) could stay out of view.
  const fitPanels = () => {
    document.querySelectorAll('.cf-pdp__panel--sticky').forEach((panel) => {
      panel.classList.toggle('is-fit', panel.offsetHeight <= window.innerHeight - 48);
    });
  };
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(fitPanels);
    const watch = () => document.querySelectorAll('.cf-pdp__panel--sticky').forEach((panel) => ro.observe(panel));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
    else watch();
  }
  window.addEventListener('resize', fitPanels, { passive: true });

  // Immediate "selected value" label update for the visual variant picker.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const group = input.closest('.cf-variants__group');
    if (!group) return;
    const label = group.querySelector('[data-cf-selected-label]');
    if (label && input.dataset.cfLabel) label.textContent = input.dataset.cfLabel;
  });
})();
